import type { Option } from '../types';

/**
 * Calculate the total price of selected options
 */
export const calculateTotalPrice = (options: Option[], currency: string): number => {
  return options.reduce((sum, option) => {
    // Skip options without price
    if (!option.price) return sum;
    return sum + option.price[currency as keyof typeof option.price];
  }, 0);
};;

/**
 * Calculate the project deadline (sum of days from selected options)
 */
export const calculateProjectDeadline = (options: Option[]): number => {
  return options.reduce((sum, option) => {
    // Only include options that have days_till_completed defined
    if (option.days_till_completed !== undefined) {
      return sum + option.days_till_completed;
    }
    return sum;
  }, 0);
};

/**
 * Format price with currency
 */
export const formatPrice = (price: number, currency: string): string => {
  const currencyConfig: Record<string, { code: string; symbol: string }> = {
    us: { code: 'USD', symbol: '$' },
    eu: { code: 'EUR', symbol: '€' },
    kzt: { code: 'KZT', symbol: '₸' },
    rub: { code: 'RUB', symbol: '₽' }
  };

  const config = currencyConfig[currency] || currencyConfig.us;

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: config.code,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(price);
};;

/**
 * Format days to human-readable format
 */
export const formatDays = (days: number): string => {
  if (days === 0) return 'No selection';

  // Format days to show decimals only if they exist
  const formattedDays = days % 1 === 0 ? days.toString() : days.toFixed(1);

  const weeks = +(days / 7).toFixed(1);
  const months = +(days / 30).toFixed(1);

  if (months >= 2) {
    const formattedMonths = months % 1 === 0 ? months.toString() : months.toFixed(1);
    return `${formattedDays} days (≈${formattedMonths} month${months >= 2 ? 's' : ''})`;
  }

  const formattedWeeks = weeks % 1 === 0 ? weeks.toString() : weeks.toFixed(1);
  return `${formattedDays} days (≈${formattedWeeks} week${weeks >= 2 ? 's' : ''})`;
};

/**
 * Check if a section has reached its selection limit
 */
export const isSectionAtLimit = (
  sectionId: string,
  selectedOptions: Option[],
  maxOptions: number
): boolean => {
  const sectionOptions = selectedOptions.filter(opt => opt.section_id === sectionId);
  return sectionOptions.length >= maxOptions;
};

/**
 * Get the count of selected options for a section
 */
export const getSectionSelectionCount = (
  sectionId: string,
  selectedOptions: Option[]
): number => {
  return selectedOptions.filter(opt => opt.section_id === sectionId).length;
};